/**
 * Servicio para el catálogo de marcas de vehículos.
 * Cuando admin o agente agregan una marca no existente al crear/editar una empresa,
 * se registra aquí para que aparezca como sugerencia en el buscador.
 */

import { doc, setDoc, getDocs, collection } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Genera un ID estable para un documento de marca (evita duplicados por nombre).
 * @param {string} nombre - Nombre de la marca
 * @returns {string} Slug para usar como doc ID
 */
function slugMarca(nombre) {
  if (!nombre || typeof nombre !== 'string') return '';
  const s = nombre
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
  return s || `marca_${Date.now()}`;
}

/**
 * Registra una marca en el catálogo Firestore (colección marcas) si no existe.
 * Usa el nombre normalizado como ID de documento para evitar duplicados.
 * Debe ser llamada cuando admin o agente agregan una marca nueva al crear/editar una empresa.
 *
 * @param {string} nombreMarca - Nombre de la marca a registrar
 * @returns {Promise<{ ok: boolean, id?: string, error?: string }>}
 */
export async function registrarMarcaEnCatalogo(nombreMarca) {
  if (!db) {
    return { ok: false, error: 'Firestore no disponible' };
  }
  const nombre = (nombreMarca || '').trim();
  if (!nombre) {
    return { ok: false, error: 'Nombre de marca vacío' };
  }

  try {
    const id = slugMarca(nombre);
    const ref = doc(db, 'marcas', id);
    await setDoc(ref, { nombre }, { merge: true });
    return { ok: true, id };
  } catch (error) {
    console.error('Error registrando marca en catálogo:', error);
    return { ok: false, error: error?.message || 'Error al registrar marca' };
  }
}

/**
 * Obtiene todas las marcas del catálogo para sugerencias en el buscador.
 * @returns {Promise<Array<string>>} Lista de nombres de marcas
 */
export async function obtenerMarcasParaSugerencias() {
  if (!db) return [];
  try {
    const snapshot = await getDocs(collection(db, 'marcas'));
    const nombres = snapshot.docs
      .map((d) => d.data().nombre)
      .filter((n) => n && typeof n === 'string' && n.trim());
    return [...new Set(nombres)].sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
  } catch (error) {
    console.error('Error obteniendo marcas para sugerencias:', error);
    return [];
  }
}
